import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../services/api';
import LogoPagaliImg from '../../assets/logo-pagali-color@2x.png';
import PhoneInput from '../../components/PhoneInput';
import { pagaliEvents, getAttribution } from '../../utils/pixel';

type AccountType = 'PESSOA' | 'EMPRESA' | 'ONG' | 'DEVELOPER' | 'PROMOTOR';
type Gender = 'M' | 'F' | 'O' | 'N';
type BusinessSubtype = 'PFLOJ' | 'PFEMP' | 'PFGOV' | 'PFONG' | 'PFEVT' | 'PFPRO';

interface BusinessType {
  code: BusinessSubtype;
  name: string;
  description: string;
}

const businessTypes: BusinessType[] = [
  { code: 'PFLOJ', name: 'Loja/Comercio', description: 'E-commerce, vendas de produtos' },
  { code: 'PFEMP', name: 'Empresa/Servicos', description: 'Prestacao de servicos' },
  { code: 'PFGOV', name: 'Governo', description: 'Entidades governamentais' },
  { code: 'PFONG', name: 'ONG/Associacao', description: 'Organizacoes sem fins lucrativos' },
  { code: 'PFEVT', name: 'Promotor de Eventos', description: 'Organizacao de eventos' },
  { code: 'PFPRO', name: 'Profissional Liberal', description: 'Freelancers e autonomos' }
];

interface FormData {
  // Common fields
  nome: string;
  email: string;
  telefone: string;
  genero: Gender;
  password: string;
  passwordConfirm: string;

  // Business fields
  subtipo?: BusinessSubtype;
  nomeNegocio?: string;
  nif?: string;

  // Developer fields
  empresa?: string;
  casoUso?: string;

  // Código da campanha (RADIO, INSTA, FACE, AMIGO) — dá acesso à oferta
  codigoOrigem: string;
}

const CHAVE_CODIGO_ORIGEM = 'pagali_codigo_origem';

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();

  const accountType = (location.state as { accountType?: AccountType })?.accountType || 'PESSOA';
  const eventoCodigo = searchParams.get('evento') || undefined;

  // Capturar UTM params do URL e guardar em sessionStorage para sobreviver multi-step
  const utm = (() => {
    const stored = sessionStorage.getItem('pagali_utm');
    const params = {
      utmSource:   searchParams.get('utm_source')   || undefined,
      utmMedium:   searchParams.get('utm_medium')   || undefined,
      utmCampaign: searchParams.get('utm_campaign') || undefined,
      utmContent:  searchParams.get('utm_content')  || undefined,
      utmTerm:     searchParams.get('utm_term')     || undefined,
      referrer:    document.referrer || undefined,
    };
    if (params.utmSource) {
      sessionStorage.setItem('pagali_utm', JSON.stringify(params));
      return params;
    }
    return stored ? JSON.parse(stored) : {};
  })();

  // Código da campanha vindo do link (?src=INSTA ou ?codigo=INSTA). Guarda-se na
  // sessão pela mesma razão das UTM: o registo tem vários passos e o parâmetro
  // perde-se do URL a meio.
  const codigoDoLink = (() => {
    const doUrl = (searchParams.get('src') || searchParams.get('codigo') || '').trim().toUpperCase();
    if (doUrl) {
      try { sessionStorage.setItem(CHAVE_CODIGO_ORIGEM, doUrl); } catch { /* sessão bloqueada */ }
      return doUrl;
    }
    try { return sessionStorage.getItem(CHAVE_CODIGO_ORIGEM) || ''; } catch { return ''; }
  })();

  const [formData, setFormData] = useState<FormData>({
    nome: '',
    email: '',
    telefone: '',
    genero: 'N',
    password: '',
    passwordConfirm: '',
    subtipo: undefined,
    nomeNegocio: '',
    nif: '',
    empresa: '',
    casoUso: '',
    codigoOrigem: codigoDoLink,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Redirect if no account type selected
  useEffect(() => {
    if (!location.state) {
      navigate('/register');
    }
  }, [location.state, navigate]);

  const getTitle = () => {
    switch (accountType) {
      case 'PESSOA': return 'Criar Conta Pessoal';
      case 'EMPRESA': return 'Criar Conta Empresa';
      case 'ONG': return 'Criar Conta ONG / Associacao';
      case 'DEVELOPER': return 'Criar Conta Developer';
      case 'PROMOTOR': return 'Criar Conta Promotor de Eventos';
      default: return 'Criar Conta';
    }
  };

  const handleInputChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome obrigatorio';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email obrigatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalido';
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telemovel obrigatorio';
    }

    if (accountType === 'EMPRESA' && !formData.subtipo) {
      newErrors.subtipo = 'Selecione o tipo de negocio';
    }
    // ONG and PROMOTOR don't need subtipo selection - they have fixed subtipos (PFONG, PFEVT)

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.password) {
      newErrors.password = 'Password obrigatoria';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Minimo 6 caracteres';
    }

    if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = 'Passwords nao coincidem';
    }

    // Business validation
    if (accountType === 'EMPRESA' || accountType === 'ONG' || accountType === 'PROMOTOR') {
      if (!formData.nomeNegocio?.trim()) {
        newErrors.nomeNegocio = accountType === 'ONG' ? 'Nome da organizacao obrigatorio' : 'Nome do negocio / evento obrigatorio';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      navigate('/register');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep2()) return;

    setIsLoading(true);

    try {
      const getTipoConta = () => {
        if (accountType === 'ONG' || accountType === 'PROMOTOR') return 'EMPRESA';
        return accountType;
      };
      const getSubtipo = () => {
        if (accountType === 'ONG') return 'PFONG';
        if (accountType === 'PROMOTOR') return 'PFEVT';
        return formData.subtipo;
      };
      const getDadosAdicionais = () => {
        if (accountType === 'EMPRESA' || accountType === 'ONG' || accountType === 'PROMOTOR') {
          return { nomeNegocio: formData.nomeNegocio, nif: formData.nif };
        }
        if (accountType === 'DEVELOPER') {
          return { empresa: formData.empresa, casoUso: formData.casoUso };
        }
        return undefined;
      };

      const response = await api.post('/registration/start', {
        tipoConta: getTipoConta(),
        subtipo: getSubtipo(),
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        genero: formData.genero,
        password: formData.password,
        dadosAdicionais: getDadosAdicionais(),
        eventoCodigo,
        ...utm,
        // Cookies do clique no anúncio. Gravados em pg_user_attribution, são o
        // que liga os eventos de servidor (KYC, 1ª transacção) à campanha.
        attribution: getAttribution(),
        codigoOrigem: accountType === 'PESSOA' && formData.codigoOrigem.trim()
          ? formData.codigoOrigem.trim()
          : undefined,
      });

      if (response.data.success) {
        showSuccess('Codigo de verificacao enviado!');
        // Aqui o registo ainda NÃO está completo — só foi enviado o OTP.
        // A conversão dispara em VerifyOTP, depois de a conta existir.
        pagaliEvents.registerStarted();
        sessionStorage.removeItem('pagali_utm');
        try { sessionStorage.removeItem(CHAVE_CODIGO_ORIGEM); } catch { /* sessão bloqueada */ }
        navigate('/register/verify', {
          state: {
            preRegistoId: response.data.data.preRegistoId,
            email: formData.email,
            telefone: formData.telefone
          }
        });
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erro ao criar conta';
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const totalSteps = 2;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBack}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <img src={LogoPagaliImg} alt="Pagali" className="h-8" />
          <div className="w-6" /> {/* Spacer */}
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i < step ? 'w-8 bg-pagali-primary' : 'w-2 bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold text-center text-gray-800 mb-2">
          {getTitle()}
        </h1>
        <p className="text-center text-gray-500 mb-8">
          {step === 1 ? 'Informacoes basicas' : 'Finalizar registo'}
        </p>

        <form onSubmit={handleSubmit}>
          {/* Step 1 - Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Business Type Selection (only for EMPRESA) */}
              {accountType === 'EMPRESA' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de negocio *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {businessTypes.map((type) => (
                      <button
                        key={type.code}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, subtipo: type.code }))}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.subtipo === type.code
                            ? 'border-pagali-primary bg-pagali-primary/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium text-sm text-gray-800">{type.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                      </button>
                    ))}
                  </div>
                  {errors.subtipo && (
                    <p className="text-red-500 text-sm mt-1">{errors.subtipo}</p>
                  )}
                </div>
              )}

              {/* Nome */}
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome completo *
                </label>
                <input
                  id="nome"
                  type="text"
                  value={formData.nome}
                  onChange={handleInputChange('nome')}
                  placeholder="Seu nome completo"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.nome ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-pagali-primary focus:border-transparent`}
                />
                {errors.nome && (
                  <p className="text-red-500 text-sm mt-1">{errors.nome}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  placeholder="seu@email.com"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-pagali-primary focus:border-transparent`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Telefone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telemóvel *
                </label>
                <PhoneInput
                  value={formData.telefone}
                  onChange={v => setFormData(prev => ({ ...prev, telefone: v }))}
                  placeholder="999 9999"
                />
                {errors.telefone && (
                  <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>
                )}
              </div>

              {/* Genero */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Genero
                </label>
                <div className="flex gap-3">
                  {[
                    { value: 'M', label: 'Masculino' },
                    { value: 'F', label: 'Feminino' },
                    { value: 'O', label: 'Outro' },
                    { value: 'N', label: 'Prefiro nao dizer' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, genero: option.value as Gender }))}
                      className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm transition-all ${
                        formData.genero === option.value
                          ? 'border-pagali-primary bg-pagali-primary/5 text-pagali-primary'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Continue Button */}
              <button
                type="button"
                onClick={handleNext}
                className="w-full bg-pagali-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-pagali-primary/90 transition-colors mt-6"
              >
                Continuar
              </button>
            </div>
          )}

          {/* Step 2 - Password & Additional Info */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Business/ONG/PROMOTOR Name */}
              {(accountType === 'EMPRESA' || accountType === 'ONG' || accountType === 'PROMOTOR') && (
                <>
                  <div>
                    <label htmlFor="nomeNegocio" className="block text-sm font-medium text-gray-700 mb-1">
                      {accountType === 'ONG' ? 'Nome da organizacao *' : accountType === 'PROMOTOR' ? 'Nome da empresa / organizacao *' : 'Nome do negocio *'}
                    </label>
                    <input
                      id="nomeNegocio"
                      type="text"
                      value={formData.nomeNegocio}
                      onChange={handleInputChange('nomeNegocio')}
                      placeholder={accountType === 'ONG' ? 'Nome da sua ONG ou associacao' : accountType === 'PROMOTOR' ? 'Nome da sua empresa ou organizacao' : 'Nome da sua empresa ou marca'}
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.nomeNegocio ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-2 focus:ring-pagali-primary focus:border-transparent`}
                    />
                    {errors.nomeNegocio && (
                      <p className="text-red-500 text-sm mt-1">{errors.nomeNegocio}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="nif" className="block text-sm font-medium text-gray-700 mb-1">
                      NIF (opcional)
                    </label>
                    <input
                      id="nif"
                      type="text"
                      value={formData.nif}
                      onChange={handleInputChange('nif')}
                      placeholder="Numero de Identificacao Fiscal"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pagali-primary focus:border-transparent"
                    />
                  </div>
                </>
              )}

              {/* Developer Fields */}
              {accountType === 'DEVELOPER' && (
                <>
                  <div>
                    <label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-1">
                      Empresa (opcional)
                    </label>
                    <input
                      id="empresa"
                      type="text"
                      value={formData.empresa}
                      onChange={handleInputChange('empresa')}
                      placeholder="Nome da sua empresa"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pagali-primary focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="casoUso" className="block text-sm font-medium text-gray-700 mb-1">
                      Como pretende usar a API?
                    </label>
                    <textarea
                      id="casoUso"
                      value={formData.casoUso}
                      onChange={handleInputChange('casoUso')}
                      placeholder="Descreva brevemente o seu projeto..."
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pagali-primary focus:border-transparent resize-none"
                    />
                  </div>
                </>
              )}

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  placeholder="Minimo 6 caracteres"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-pagali-primary focus:border-transparent`}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar Password *
                </label>
                <input
                  id="passwordConfirm"
                  type="password"
                  value={formData.passwordConfirm}
                  onChange={handleInputChange('passwordConfirm')}
                  placeholder="Repita a password"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.passwordConfirm ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-pagali-primary focus:border-transparent`}
                />
                {errors.passwordConfirm && (
                  <p className="text-red-500 text-sm mt-1">{errors.passwordConfirm}</p>
                )}
              </div>

              {/* Código da campanha — só contas particulares entram na oferta */}
              {accountType === 'PESSOA' && (
                <div>
                  <label htmlFor="codigoOrigem" className="block text-sm font-medium text-gray-700 mb-1">
                    Código da campanha (opcional)
                  </label>
                  <input
                    id="codigoOrigem"
                    type="text"
                    value={formData.codigoOrigem}
                    onChange={(e) => setFormData(prev => ({ ...prev, codigoOrigem: e.target.value.toUpperCase() }))}
                    placeholder="Ex.: INSTA, RADIO, FACE, AMIGO"
                    maxLength={16}
                    autoCapitalize="characters"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pagali-primary focus:border-transparent uppercase"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ouviste falar de nós na rádio, no Instagram, no Facebook ou por um amigo? Escreve o código.
                  </p>
                </div>
              )}

              {/* Terms */}
              <p className="text-xs text-gray-500 mt-4">
                Ao criar uma conta, concorda com os nossos{' '}
                <Link to="/terms" className="text-pagali-primary hover:underline">
                  Termos de Uso
                </Link>{' '}
                e{' '}
                <Link to="/privacy" className="text-pagali-primary hover:underline">
                  Politica de Privacidade
                </Link>.
              </p>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-pagali-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-pagali-primary/90 transition-colors mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    A processar...
                  </span>
                ) : (
                  'Criar conta'
                )}
              </button>
            </div>
          )}
        </form>

        {/* Login Link */}
        <p className="text-center mt-6 text-gray-600">
          Ja tem conta?{' '}
          <Link to="/login" className="text-pagali-primary font-medium hover:underline">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;
